import { AccountActions, Permission, Product } from '@/models'
import { VNode } from 'vue'
import { DirectiveBinding } from 'vue/types/options'
import { VueConstructor } from 'vue/types/umd'
import store from '@/store'
import router from '@/router'
import {
  APP_CCP,
  ROLE_ADMIN,
  ROLE_SPECIAL,
  ROLE_EDITOR,
  ROLE_READ,
  ROLE_ACCESS,
  SECURITY_ACTION_REMOVE,
  SECURITY_ACTION_DISABLE
} from '@/utils/constants'
import { ActionTypes } from '@/store/action-types'
import { Utils } from '@/utils'

const ROLES_ARRAY_SPECIAL = [ROLE_ADMIN, ROLE_SPECIAL]
const ROLES_ARRAY_EDITOR = [ROLE_ADMIN, ROLE_SPECIAL, ROLE_EDITOR]
const ROLES_ARRAY_READ = [ROLE_ADMIN, ROLE_SPECIAL, ROLE_EDITOR, ROLE_READ]
const DISPLAY_NONE = 'none'

function getParamsFromElementHtml(element: HTMLElement) {
  return {
    styleDisplay: element.style.display
  }
}

function addSecurityVisualIndicators(element: HTMLElement, role: string, actionId?: string) {
  // Add CSS class for any element with an action ID
  if (actionId) {
    element.classList.add('security-action')
  }

  // Add CSS class based on role
  if (role === ROLE_SPECIAL) {
    element.classList.add('security-special')
  } else if (role === ROLE_ADMIN) {
    element.classList.add('security-admin')
  }
}

function restoreElementHtml(element: HTMLElement, action: string, paramsForRestoreElementHtml: any, role?: string, actionId?: string) {
  switch (action) {
    case SECURITY_ACTION_DISABLE:
      (element as any).disabled = false
      break
    case SECURITY_ACTION_REMOVE:
      element.style.display = paramsForRestoreElementHtml.styleDisplay === DISPLAY_NONE ? '' : paramsForRestoreElementHtml.styleDisplay
      break
  }

  // Add visual indicators for SPECIAL and ADMIN roles, and button actions
  if (role) {
    addSecurityVisualIndicators(element, role, actionId)
  }
}

function blockElementHtml(element: HTMLElement, action: string, alternative?: string) {
  switch (action) {
    case SECURITY_ACTION_DISABLE:
      (element as any).disabled = true
      break
    case SECURITY_ACTION_REMOVE:
      element.style.display = DISPLAY_NONE
      break
  }
}

async function securityDirective(el: HTMLElement, binding: DirectiveBinding, vnode: VNode, oldVnode: VNode) {
  const roleGiven = binding.value.role
  const product = binding.value.product as Product
  const actionGiven = binding.value.action ? binding.value.action : SECURITY_ACTION_REMOVE
  const alternativeElement = binding.value.alternative
  const idGiven = binding.value.id
  const paramsForRestoreElementHtml = getParamsFromElementHtml(el)

  // Block element, previous check permission (disable, hide, etc...)
  blockElementHtml(el, actionGiven, alternativeElement)

  // Load user data if not loaded
  if (!store.state.user.username) {
    // Is not logged in
    if (!store.getters.isUserAuthenticated) {
      return
    }
    await store.dispatch(ActionTypes.USER_REQUEST)
  }

  // Check if user has no account or account is disabled
  // In both cases: allow access up to EDITOR role (not ADMIN or SPECIAL)
  const hasNoAccount = !store.state.user.account || !store.state.user.account.accountId
  const isAccountDisabled = store.state.user.account && store.state.user.account.active === false

  if (hasNoAccount || isAccountDisabled) {
    if (roleGiven !== ROLE_ADMIN && roleGiven !== ROLE_SPECIAL) {
      restoreElementHtml(el, actionGiven, paramsForRestoreElementHtml, roleGiven, idGiven)
    }
    return
  }

  // From here: user has active account -> Apply normal permissions

  // Filter permissions by APP
  const permissions = store.state.user.account.permissions.filter((item: Permission) => { return item.appId === APP_CCP })
  if (Utils.hasCcpRole(store.state.user.account, ROLE_ADMIN)) {
    restoreElementHtml(el, actionGiven, paramsForRestoreElementHtml, roleGiven, idGiven)
    return
  }

  // Check id given
  if (idGiven) {
    const accountAction = store.state.user.account.actions.find((item: AccountActions) => { return item.actionsId === idGiven })
    if (!accountAction) {
      return
    }
  }

  // Check roles
  if (roleGiven === ROLE_ADMIN) {
    const permission = permissions.find((item: Permission) => { return item.role === ROLE_ADMIN })
    if (!permission) {
      return
    }
  }

  if (roleGiven === ROLE_SPECIAL) {
    let permission = null
    if (product && product.editorialProject) {
      permission = permissions.find((item: Permission) => {
        return ROLES_ARRAY_SPECIAL.includes(item.role) && item.editorialProjectId === product.editorialProject
      })
    } else {
      permission = permissions.find((item: Permission) => {
        return ROLES_ARRAY_SPECIAL.includes(item.role)
      })
    }

    if (!permission) {
      return
    }
  }

  if (roleGiven === ROLE_EDITOR) {
    let permission = null
    if (product && product.editorialProject) {
      permission = permissions.find((item: Permission) => {
        return ROLES_ARRAY_EDITOR.includes(item.role) && item.editorialProjectId === product.editorialProject
      })
    } else {
      permission = permissions.find((item: Permission) => {
        return ROLES_ARRAY_EDITOR.includes(item.role)
      })
    }

    if (!permission) {
      return
    }
  }

  if (roleGiven === ROLE_READ) {
    let permission = null
    if (product && product.editorialProject) {
      permission = permissions.find((item: Permission) => {
        return ROLES_ARRAY_READ.includes(item.role) && item.editorialProjectId === product.editorialProject
      })
    } else {
      permission = permissions.find((item: Permission) => {
        return ROLES_ARRAY_READ.includes(item.role)
      })
    }

    if (!permission) {
      return
    }
  }

  if (roleGiven === ROLE_ACCESS) {
    const permission = permissions.find((item: Permission) => { return item.role === ROLE_ACCESS })
    if (!permission) {
      router.push('/unauthorized')
    }
  }

  // Restore previous estate in element, for allow user execute actions
  restoreElementHtml(el, actionGiven, paramsForRestoreElementHtml, roleGiven, idGiven)
}

/**
 * Visibility by account profile (ADMIN / MANAGER / USER), independent from the CCP
 * app-permission model used by [securityDirective]. This mirrors the router's
 * `meta.requiredProfiles` guard so the menu matches what each route actually allows.
 *
 * Usage: v-profile="{ profiles: [ACCOUNT_PROFILE_ADMIN, ACCOUNT_PROFILE_MANAGER] }"
 */
async function profileDirective(el: HTMLElement, binding: DirectiveBinding, vnode: VNode, oldVnode: VNode) {
  const profilesGiven = (binding.value.profiles ?? []) as string[]
  const actionGiven = binding.value.action ? binding.value.action : SECURITY_ACTION_REMOVE
  const paramsForRestoreElementHtml = getParamsFromElementHtml(el)

  // Hide first, reveal only once the profile is confirmed.
  blockElementHtml(el, actionGiven)

  // Load user data if not loaded
  if (!store.state.user.username) {
    if (!store.getters.isUserAuthenticated) {
      return
    }
    await store.dispatch(ActionTypes.USER_REQUEST)
  }

  const profile = store.state.user.account?.profile
  if (profile && profilesGiven.includes(profile)) {
    restoreElementHtml(el, actionGiven, paramsForRestoreElementHtml)
  }
}

export default {
  install(Vue: VueConstructor) {
    Vue.directive('security', {
      update: function(el: HTMLElement, binding: DirectiveBinding, vnode: VNode, oldVnode: VNode) {
        securityDirective(el, binding, vnode, oldVnode)
      },
      inserted: function(el: HTMLElement, binding: DirectiveBinding, vnode: VNode, oldVnode: VNode) {
        securityDirective(el, binding, vnode, oldVnode)
      }
    })

    Vue.directive('profile', {
      update: function(el: HTMLElement, binding: DirectiveBinding, vnode: VNode, oldVnode: VNode) {
        profileDirective(el, binding, vnode, oldVnode)
      },
      inserted: function(el: HTMLElement, binding: DirectiveBinding, vnode: VNode, oldVnode: VNode) {
        profileDirective(el, binding, vnode, oldVnode)
      }
    })
  }
}
